import React, { useState } from 'react';
import { authService } from '../../auth/authService';
import Button from '../ui/Button';
import ThemeSettings from '../ThemeSettings';
import SelkirkIcon from '../icons/SelkirkIcon';
import { ICON } from '../../icons/iconIds';
import './TopNavigation.css';

const TopNavigation = ({ 
  user, 
  onLogout, 
  onToggleSidebar, 
  sidebarCollapsed = false,
  showSidebarToggle = true 
}) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [themeSettingsOpen, setThemeSettingsOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await authService.logout();
      onLogout?.();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    setNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    setUserMenuOpen(false);
  };

  // Mock notifications - replace with real data
  const notifications = [
    { id: 1, title: 'System Update', message: 'New features available', time: '5m ago', read: false },
    { id: 2, title: 'Data Sync', message: 'Drilling data synchronized', time: '1h ago', read: true },
    { id: 3, title: 'Report Ready', message: 'Monthly report generated', time: '2h ago', read: true },
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
    <nav className="top-navigation">
      <div className="nav-left">
        {showSidebarToggle && (
          <button
            className="sidebar-toggle"
            onClick={onToggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <div className={`hamburger ${sidebarCollapsed ? 'collapsed' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        )}
        
        <div className="brand">
          <div className="brand-logo">
            <img 
              src="/selkirk-logo.png" 
              alt="Selkirk Copper" 
              className="logo-icon"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="logo-fallback" style={{ display: 'none' }}>
              <span className="logo-text">SC</span>
            </div>
          </div>
          <div className="brand-text">
            <span className="brand-name">Selkirk Copper</span>
            <span className="brand-subtitle">Data Platform</span>
          </div>
        </div>
      </div>

      <div className="nav-center">
        {/* Search bar - can be added later */}
      </div>

      <div className="nav-right">
        {/* Notifications */}
        <div className="nav-item notifications">
          <button
            className={`nav-button ${notificationsOpen ? 'active' : ''}`}
            onClick={toggleNotifications}
            aria-label="Notifications"
          >
            <span className="notification-icon">
              <SelkirkIcon name={ICON.navNotifications} variant="nav" decorative />
            </span>
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>
          
          {notificationsOpen && (
            <div className="notifications-dropdown">
              <div className="dropdown-header">
                <h3>Notifications</h3>
                <span className="notification-count">{unreadCount} new</span>
              </div>
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                  >
                    <div className="notification-content">
                      <h4>{notification.title}</h4>
                      <p>{notification.message}</p>
                      <span className="notification-time">{notification.time}</span>
                    </div>
                    {!notification.read && <div className="unread-dot"></div>}
                  </div>
                ))}
              </div>
              <div className="dropdown-footer">
                <button className="view-all-btn">View All Notifications</button>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="nav-item user-menu">
          <button
            className={`user-button ${userMenuOpen ? 'active' : ''}`}
            onClick={toggleUserMenu}
            aria-label="User menu"
          >
            <div className="user-avatar">
              {user?.photoUrl ? (
                <img 
                  src={user.photoUrl} 
                  alt={user?.name || 'User'} 
                  className="avatar-image"
                />
              ) : (
                <span className="avatar-text">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            <div className="user-info">
              <span className="user-name">{user?.name || 'User'}</span>
              <span className="user-email">{user?.email || user?.username}</span>
            </div>
            <span className={`dropdown-arrow ${userMenuOpen ? 'open' : ''}`}>
              <SelkirkIcon name={ICON.navChevronDown} size={12} decorative />
            </span>
          </button>

          {userMenuOpen && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <div className="user-avatar large">
                  {user?.photoUrl ? (
                    <img 
                      src={user.photoUrl} 
                      alt={user?.name || 'User'} 
                      className="avatar-image"
                    />
                  ) : (
                    <span className="avatar-text">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div className="user-details">
                  <h3>{user?.name || 'User'}</h3>
                  <p>{user?.email || user?.username}</p>
                </div>
              </div>
              
              <div className="dropdown-menu">
                <button className="menu-item">
                  <span className="menu-icon">
                    <SelkirkIcon name={ICON.navProfile} variant="nav" decorative />
                  </span>
                  Profile Settings
                </button>
                <button 
                  className="menu-item"
                  onClick={() => {
                    setThemeSettingsOpen(true);
                    setUserMenuOpen(false);
                  }}
                >
                  <span className="menu-icon">
                    <SelkirkIcon name={ICON.navTheme} variant="nav" decorative />
                  </span>
                  Theme Preferences
                </button>
                <button className="menu-item">
                  <span className="menu-icon">
                    <SelkirkIcon name={ICON.navSettings} variant="nav" decorative />
                  </span>
                  Account Settings
                </button>
                <div className="menu-divider"></div>
                <button className="menu-item">
                  <span className="menu-icon">
                    <SelkirkIcon name={ICON.navHelp} variant="nav" decorative />
                  </span>
                  Help & Support
                </button>
                <button className="menu-item danger" onClick={handleLogout}>
                  <span className="menu-icon">
                    <SelkirkIcon name={ICON.navSignOut} variant="nav" decorative />
                  </span>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay to close dropdowns when clicking outside */}
      {(userMenuOpen || notificationsOpen) && (
        <div 
          className="nav-overlay"
          onClick={() => {
            setUserMenuOpen(false);
            setNotificationsOpen(false);
          }}
        />
      )}
    </nav>

    {/* Render outside nav so position:fixed overlay uses the viewport, not the 4rem bar */}
    {themeSettingsOpen && (
      <ThemeSettings onClose={() => setThemeSettingsOpen(false)} />
    )}
    </>
  );
};

export default TopNavigation;
